/**
 * Data Model - Tree operations for CRDT reconstruction
 *
 * ARCHITECTURE NOTE (CRDT-First):
 *
 * This module provides low-level tree operations used internally by
 * YjsDocManager during CRDT reconstruction.
 *
 * DO NOT call mutation functions (addNode, updateNode, removeNode, moveNode)
 * directly from application code. All mutations must go through CRDTBridge
 * to ensure proper CRDT synchronization.
 *
 * Safe to use directly: find(), findParent(), getPath(), getAll(), getLeaves(),
 * cleanNode(), serialize(), deserialize()
 *
 * ID Generation: For new code, prefer crdtBridge.generateNodeId() (via modules.js).
 * The generateId() here exists for backwards compatibility and internal fallback.
 *
 * The tree structure:
 * {
 *   id: string,
 *   name: string,
 *   children: Node[],
 *   color?: string,
 *   notes?: string[],
 *   status?: 'todo'|'in-progress'|'done',
 *   startDate?: string,
 *   endDate?: string,
 *   duration?: number,
 *   effort?: number,
 *   effortUnit?: 'hours'|'days',
 *   priority?: 'low'|'medium'|'high',
 *   assignee?: string,
 *   link?: string,
 *   mmX?: number,      // Mindmap position
 *   mmY?: number,
 *   mmSide?: 'left'|'right'
 * }
 */

// Internal root reference - no external state dependency
let _root = null;

// Lazy id→node and id→parent indexes over _root. find()/findParent()/
// getPath() were O(n) recursive scans and are called from hot loops
// (sidebar drag, keyboard nav, breadcrumbs — often per pointer event).
// Built on first indexed lookup after a change; invalidated by setRoot()
// and by structural mutations (addNode/removeNode/moveNode). Property
// updates (updateNode) mutate nodes in place, preserving identity, so
// they don't invalidate. Verified: no code outside this module mutates
// the live tree's children arrays directly.
let _nodeIndex = null;
let _parentIndex = null;

function invalidateIndex() {
  _nodeIndex = null;
  _parentIndex = null;
}

function ensureIndex() {
  if (_nodeIndex) return;
  _nodeIndex = new Map();
  _parentIndex = new Map();
  if (!_root) return;
  const walk = (node, parent) => {
    _nodeIndex.set(node.id, node);
    _parentIndex.set(node.id, parent);
    for (const child of node.children || []) {
      walk(child, node);
    }
  };
  walk(_root, null);
}

import { generateUUID } from "./uuid.js";
import { t } from "../../shared/i18n/index.js";

// ============================================
// Root Management
// ============================================

/**
 * Set the root node of the tree
 * @param {Object|null} root - Root node
 */
export function setRoot(root) {
  _root = root;
  invalidateIndex();
}

/**
 * Get the current root node
 * @returns {Object|null} Root node
 */
export function getRoot() {
  return _root;
}

// ============================================
// Node ID Generation
// ============================================

/**
 * Generate a unique node ID
 * @returns {string} UUID
 */
export function generateId() {
  return generateUUID();
}

// ============================================
// Tree Traversal & Queries
// ============================================

/**
 * Find a node by ID in the tree
 * @param {string} id - Node ID
 * @param {Object} root - Root node (defaults to internal _root)
 * @returns {Object|null} Found node or null
 */
export function find(id, root = null) {
  // Indexed fast path for the default (whole-tree) lookup
  if (root === null || root === _root) {
    if (!_root || !id) return null;
    ensureIndex();
    return _nodeIndex.get(id) || null;
  }
  // Custom subtree: recursive scan (semantics = "find within this subtree")
  return _findScan(id, root);
}

function _findScan(id, root) {
  if (!root || !id) return null;
  if (root.id === id) return root;

  for (const child of root.children || []) {
    const found = _findScan(id, child);
    if (found) return found;
  }
  return null;
}

/**
 * Find the parent of a node
 * @param {string} id - Node ID
 * @param {Object} root - Root node
 * @returns {Object|null} Parent node or null
 */
export function findParent(id, root = null) {
  if (root === null || root === _root) {
    if (!_root || !id) return null;
    ensureIndex();
    return _parentIndex.get(id) || null;
  }
  return _findParentScan(id, root);
}

function _findParentScan(id, root) {
  if (!root || !id) return null;

  for (const child of root.children || []) {
    if (child.id === id) return root;
    const found = _findParentScan(id, child);
    if (found) return found;
  }
  return null;
}

/**
 * Get the path from root to a node
 * @param {string} id - Node ID
 * @param {Object} root - Root node
 * @returns {Object[]} Array of nodes from root to target
 */
export function getPath(id, root = null) {
  // Indexed fast path: walk the parent chain and reverse
  if (root === null || root === _root) {
    if (!_root || !id) return [];
    ensureIndex();
    let node = _nodeIndex.get(id);
    if (!node) return [];
    const path = [];
    while (node) {
      path.push(node);
      node = _parentIndex.get(node.id) || null;
    }
    return path.reverse();
  }
  return _getPathScan(id, root);
}

function _getPathScan(id, root) {
  if (!root || !id) return [];
  if (root.id === id) return [root];

  for (const child of root.children || []) {
    const path = _getPathScan(id, child);
    if (path.length > 0) {
      return [root, ...path];
    }
  }
  return [];
}

/**
 * Check if a node is a descendant of another
 * @param {string} descendantId - Potential descendant ID
 * @param {string} ancestorId - Potential ancestor ID
 * @returns {boolean}
 */
function isDescendant(descendantId, ancestorId) {
  const ancestor = find(ancestorId);
  if (!ancestor) return false;
  return find(descendantId, ancestor) !== null && descendantId !== ancestorId;
}

/**
 * Get all nodes in the tree as a flat array
 * @param {Object} root - Root node
 * @returns {Object[]} All nodes
 */
export function getAll(root = null) {
  root = root || _root;
  if (!root) return [];

  const nodes = [root];
  for (const child of root.children || []) {
    nodes.push(...getAll(child));
  }
  return nodes;
}

/**
 * Get all leaf nodes (nodes with no children)
 * @param {Object} root - Root node
 * @returns {Object[]} Leaf nodes
 */
export function getLeaves(root = null) {
  root = root || _root;
  if (!root) return [];

  if (!root.children || root.children.length === 0) {
    return [root];
  }

  const leaves = [];
  for (const child of root.children) {
    leaves.push(...getLeaves(child));
  }
  return leaves;
}

// ============================================
// Node Operations (with change tracking)
// ============================================

/**
 * Add a new node as a child
 * @param {string} parentId - Parent node ID
 * @param {Object} nodeData - Node data (id will be generated if not provided)
 * @param {number} index - Position in children array (-1 for end)
 * @returns {Object|null} The created node or null on failure
 */
export function addNode(parentId, nodeData, index = -1) {
  const parent = find(parentId);
  if (!parent) {
    console.warn("[DataModel] Parent not found:", parentId);
    return null;
  }

  // Ensure node has required fields
  const node = {
    id: nodeData.id || generateId(),
    name: nodeData.name || t("sync.node.defaultName"),
    children: nodeData.children || [],
    ...nodeData,
  };

  // Ensure parent has children array
  if (!parent.children) {
    parent.children = [];
  }

  // Insert at position
  if (index >= 0 && index < parent.children.length) {
    parent.children.splice(index, 0, node);
  } else {
    parent.children.push(node);
    index = parent.children.length - 1;
  }

  invalidateIndex(); // structural change (node may carry a subtree)
  return node;
}

/**
 * Update a node's properties
 * @param {string} nodeId - Node ID
 * @param {Object} updates - Properties to update
 * @returns {boolean} Success
 */
export function updateNode(nodeId, updates) {
  const node = find(nodeId);
  if (!node) {
    console.warn("[DataModel] Node not found:", nodeId);
    return false;
  }

  // Track old values for undo and CRDT
  const oldValues = {};
  for (const key of Object.keys(updates)) {
    oldValues[key] = node[key];
  }

  // Apply updates
  Object.assign(node, updates);

  return true;
}

/**
 * Remove a node and all its descendants
 * @param {string} nodeId - Node ID to remove
 * @returns {Object|null} Removed node or null
 */
export function removeNode(nodeId) {
  // Can't remove root
  if (_root && _root.id === nodeId) {
    console.warn("[DataModel] Cannot remove root node");
    return null;
  }

  const parent = findParent(nodeId);
  if (!parent) {
    console.warn("[DataModel] Parent not found for:", nodeId);
    return null;
  }

  const index = parent.children.findIndex((c) => c.id === nodeId);
  if (index === -1) return null;

  // Remove from parent
  const [removed] = parent.children.splice(index, 1);

  invalidateIndex(); // structural change (whole subtree removed)
  return removed;
}

/**
 * Move a node to a new parent
 * @param {string} nodeId - Node to move
 * @param {string} newParentId - New parent ID
 * @param {number} index - Position in new parent's children (-1 for end)
 * @returns {boolean} Success
 */
export function moveNode(nodeId, newParentId, index = -1) {
  // Can't move root
  if (_root && _root.id === nodeId) {
    console.warn("[DataModel] Cannot move root node");
    return false;
  }

  // Can't move to self or descendant
  if (nodeId === newParentId || isDescendant(newParentId, nodeId)) {
    console.warn("[DataModel] Cannot move node to itself or descendant");
    return false;
  }

  const oldParent = findParent(nodeId);
  const newParent = find(newParentId);

  if (!oldParent || !newParent) {
    console.warn("[DataModel] Parent not found");
    return false;
  }

  // Find and remove from old parent
  const oldIndex = oldParent.children.findIndex((c) => c.id === nodeId);
  if (oldIndex === -1) return false;

  const [node] = oldParent.children.splice(oldIndex, 1);

  // Ensure new parent has children array
  if (!newParent.children) {
    newParent.children = [];
  }

  // Insert into new parent
  if (index >= 0 && index < newParent.children.length) {
    newParent.children.splice(index, 0, node);
  } else {
    newParent.children.push(node);
    index = newParent.children.length - 1;
  }

  invalidateIndex(); // structural change (parent links shifted)
  return true;
}

/**
 * Create a sibling node (before or after current)
 * @param {string} siblingId - Reference sibling ID
 * @param {Object} nodeData - New node data
 * @param {boolean} after - Insert after (true) or before (false)
 * @returns {Object|null} Created node
 */
export function addSibling(siblingId, nodeData = {}, after = true) {
  const parent = findParent(siblingId);
  if (!parent) {
    console.warn("[DataModel] Cannot add sibling to root");
    return null;
  }

  const siblingIndex = parent.children.findIndex((c) => c.id === siblingId);
  const insertIndex = after ? siblingIndex + 1 : siblingIndex;

  return addNode(parent.id, nodeData, insertIndex);
}

/**
 * Create a child node
 * @param {string} parentId - Parent node ID
 * @param {Object} nodeData - New node data
 * @returns {Object|null} Created node
 */
export function addChild(parentId, nodeData = {}) {
  return addNode(parentId, nodeData, -1);
}

// ============================================
// Serialization
// ============================================

/**
 * Clean a node for serialization (remove computed properties)
 * @param {Object} node - Node to clean
 * @returns {Object} Cleaned node
 */
export function cleanNode(node) {
  if (!node) return null;

  const clean = { ...node };

  // Remove computed/transient properties
  delete clean._size;
  delete clean._depth;
  delete clean._x;
  delete clean._y;

  // Recursively clean children
  if (clean.children) {
    clean.children = clean.children.map((child) => cleanNode(child));
  }

  return clean;
}

/**
 * Serialize tree to JSON string
 * @param {Object} root - Root node (defaults to internal _root)
 * @returns {string} JSON string
 */
export function serialize(root = null) {
  root = root || _root;
  return JSON.stringify(cleanNode(root));
}

/**
 * Deserialize JSON to tree
 * @param {string} json - JSON string
 * @returns {Object} Root node
 */
export function deserialize(json) {
  try {
    return JSON.parse(json);
  } catch (e) {
    console.error("[DataModel] Failed to deserialize:", e);
    return null;
  }
}
